import * as React from 'react';

import useResizeObserver from 'use-resize-observer';

export type FacilitiesCellProps = {
  facilities: string[];
  minVisible?: number;
  estimatedCharWidth?: number;
};

const SEPARATOR = ', ';
const OVERFLOW_PREFIX = '... +';

const estimateTextWidth = (text: string, charWidth: number): number =>
  text.length * charWidth;

export const FacilitiesCell = ({
  facilities,
  minVisible = 1,
  estimatedCharWidth = 7,
}: FacilitiesCellProps) => {
  const containerRef = React.useRef<HTMLDivElement>(null);

  const { width: containerWidth } = useResizeObserver<HTMLDivElement>({
    ref: containerRef as React.RefObject<HTMLDivElement>,
  });

  const [visibleCount, setVisibleCount] = React.useState(facilities.length);

  React.useLayoutEffect(() => {
    if (!containerWidth || facilities.length === 0) {
      setVisibleCount(facilities.length);
      return;
    }

    const calculateVisibleCount = () => {
      const separatorWidth = estimateTextWidth(SEPARATOR, estimatedCharWidth);
      const maxOverflowWidth = estimateTextWidth(
        `${OVERFLOW_PREFIX}99`,
        estimatedCharWidth,
      );

      const computeCount = (): number => {
        let totalWidth = 0;

        for (let i = 0; i < facilities.length; i++) {
          const facilityWidth = estimateTextWidth(
            facilities[i],
            estimatedCharWidth,
          );
          const remainingItems = facilities.length - i - 1;
          const needsOverflow = remainingItems > 0;
          const needsSeparator = i > 0;

          const projectedWidth =
            totalWidth +
            (needsSeparator ? separatorWidth : 0) +
            facilityWidth +
            (needsOverflow ? maxOverflowWidth : 0);

          if (projectedWidth > containerWidth && i >= minVisible) {
            return i;
          }

          totalWidth += (needsSeparator ? separatorWidth : 0) + facilityWidth;
        }

        return facilities.length;
      };

      setVisibleCount(Math.max(minVisible, computeCount()));
    };

    requestAnimationFrame(calculateVisibleCount);
  }, [containerWidth, facilities, minVisible, estimatedCharWidth]);

  if (facilities.length === 0) {
    return <span className="text-neutral/50">-</span>;
  }

  const visibleFacilities = facilities.slice(0, visibleCount);
  const overflowCount = facilities.length - visibleCount;

  return (
    <div
      ref={containerRef}
      className="@FacilitiesCell flex items-center overflow-hidden"
    >
      <span className="truncate">{visibleFacilities.join(SEPARATOR)}</span>
      {overflowCount > 0 && (
        <span className="shrink-0 text-neutral/50">
          {OVERFLOW_PREFIX}
          {overflowCount}
        </span>
      )}
    </div>
  );
};
